import { useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PluginServiceConfigField, PluginServiceConfigValues } from '@reflex/shared';
import { ActionRow } from '@reflex/ui/components/shared/ActionRow';
import { Switch } from '@reflex/ui/components/ui/switch';
import { toErrorMessage } from '@reflex/plugin-api/errors';
import { toast } from 'sonner';
import { IconSectionHeader } from '@/components/shared/IconSectionHeader';
import { api } from '@/lib/api';

interface PluginServiceConfigSectionProps {
  pluginName: string;
  fields: PluginServiceConfigField[];
  values: PluginServiceConfigValues;
}

/**
 * Editable service-level (deployment-wide) configuration for a plugin, shown
 * on the admin plugin detail page. Each declared field renders as a toggle;
 * flipping one persists immediately via the admin service-config route and
 * refreshes the plugin manifest. The first consumer is the `auth-email`
 * plugin's "allow email registration" switch.
 */
export function PluginServiceConfigSection({
  pluginName,
  fields,
  values,
}: PluginServiceConfigSectionProps) {
  const queryClient = useQueryClient();
  // Optimistic local copy so the toggle responds instantly; reconciled to the
  // server response on success and rolled back on error.
  const [local, setLocal] = useState<PluginServiceConfigValues>(values);

  const mutation = useMutation({
    mutationFn: (next: PluginServiceConfigValues) =>
      api.updatePluginServiceConfig(pluginName, next),
    onMutate: (next: PluginServiceConfigValues) => {
      const previous = local;
      setLocal(next);
      return { previous };
    },
    onSuccess: (view) => {
      setLocal(view.values);
      // Manifest carries serviceConfig.values; refresh so other surfaces agree.
      void queryClient.invalidateQueries({ queryKey: ['plugins'] });
    },
    onError: (err, _next, ctx) => {
      if (ctx?.previous) setLocal(ctx.previous);
      toast.error('Could not update configuration', {
        description: toErrorMessage(err),
      });
    },
  });

  if (fields.length === 0) return null;

  return (
    <section data-testid="plugin-detail-section-service-config">
      <IconSectionHeader icon={SlidersHorizontal}>Service configuration</IconSectionHeader>
      <div className="divide-y rounded-lg border">
        {fields.map((field) => {
          const checked = local[field.key] ?? field.default;
          return (
            <ActionRow
              key={field.key}
              data-testid={`plugin-service-config-row-${field.key}`}
              title={field.label}
              description={field.description}
              actions={
                <Switch
                  checked={checked}
                  disabled={mutation.isPending}
                  aria-label={field.label}
                  onCheckedChange={(value) => mutation.mutate({ ...local, [field.key]: value })}
                />
              }
            />
          );
        })}
      </div>
    </section>
  );
}
